import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ExternalLink, FileSearch, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import QuoteParseReviewSheet from '@/components/sheets/QuoteParseReviewSheet.jsx';
import { formatDateOrEmpty } from '@/lib/date-display.js';
import {
  getQuoteInbox,
  runQuoteParse,
  assignDocToDeal,
} from '@/services/quoteIngest.service.js';

// MV-03: the quote ingestion inbox. Two sections:
//   • Parse drafts — staged quotes awaiting review-approve (the one action:
//     Review parse). Approving writes the quote; nothing else does.
//   • Needs a match — quote-like documents the matcher could not confidently
//     place; the broker picks the deal (from candidates or by pasting a deal
//     id) and the pipeline re-runs. Gmail-arrived items also carry "Open in
//     Gmail" (TRACON indexes and deep-links, it never re-hosts mail).

// Deep link to the exact message in the broker's Gmail. Message ids from the
// Gmail API are hex and address the message directly in the UI.
export function gmailDeepLink(gmailMessageId) {
  if (!gmailMessageId || String(gmailMessageId).includes(':')) return null; // synthetic ids (drop-pdf:, carrier-doc:) have no mailbox home
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(gmailMessageId)}`;
}

function TriageRow({ doc, onResolved }) {
  const [assigning, setAssigning] = useState(false);
  const [manualDealId, setManualDealId] = useState('');
  const link = gmailDeepLink(doc.gmail_message_id);

  async function assignAndParse(dealId) {
    if (!dealId) return;
    setAssigning(true);
    try {
      await assignDocToDeal(doc.id, dealId);
      const parse = await runQuoteParse(doc.id);
      if (parse.staged) {
        toast.success('Draft staged — review it from Parse drafts');
      } else if (parse.duplicate_of) {
        toast.success('Already captured: terms match an existing quote on that deal');
      } else {
        toast.error('Assigned, but the parse still did not stage — check the document');
      }
      onResolved?.();
    } catch (err) {
      toast.error(err.body?.error || err.message || 'Assign failed (parse alert fired if it was a parse failure)');
    } finally {
      setAssigning(false);
    }
  }

  return (
    <div className="rounded-card border border-border bg-surface px-3.5 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text">{doc.subject || doc.filename || 'Quote document'}</p>
          <p className="text-[11px] text-text-muted">
            {doc.carrier || 'Unknown carrier'}
            {doc.insured ? ` · ${doc.insured}` : ''}
            {doc.tails?.length ? ` · ${doc.tails.join(', ')}` : ''}
            {doc.from_address ? ` · from ${doc.from_name || doc.from_address}` : ''}
          </p>
        </div>
        <Badge variant="warning">needs a match</Badge>
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-[5px] border border-border px-2.5 py-1 text-[12px] text-text hover:border-border-strong"
          >
            <Mail size={13} strokeWidth={1.5} /> Open in Gmail <ExternalLink size={11} strokeWidth={1.5} />
          </a>
        ) : null}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {(doc.match_candidates || []).map((c) => (
          <Button
            key={c.crm_deal_id}
            variant="outline"
            size="xs"
            disabled={assigning}
            onClick={() => assignAndParse(c.crm_deal_id)}
          >
            {c.account_name || c.crm_deal_id} · {(c.signals || []).join('+') || 'candidate'}
          </Button>
        ))}
        <input
          placeholder="…or paste a deal id"
          value={manualDealId}
          onChange={(e) => setManualDealId(e.target.value)}
          className="w-44 rounded border border-border bg-bg px-2 py-1 text-[12px] text-text"
        />
        <Button variant="outline" size="xs" disabled={assigning || !manualDealId.trim()} onClick={() => assignAndParse(manualDealId.trim())}>
          {assigning ? 'Working…' : 'Assign + parse'}
        </Button>
      </div>
    </div>
  );
}

// Front door C item: detection only — extraction has not run. Two actions:
// Open in Gmail (the reading surface stays Gmail) and Review parse (runs the
// gated extraction, then opens the review screen or lands in triage).
function DetectedRow({ doc, onStaged, onResolved }) {
  const [parsing, setParsing] = useState(false);
  const link = gmailDeepLink(doc.gmail_message_id);

  async function reviewParse() {
    setParsing(true);
    try {
      const parse = await runQuoteParse(doc.id);
      if (parse.staged && parse.quote_id) {
        onStaged?.(parse.quote_id);
      } else if (parse.duplicate_of) {
        toast.success('Already captured: terms match an existing quote');
      } else if (parse.not_quote) {
        toast.error(`Parsed as "${parse.type}", not a quote — moved to the inbound queue`);
      } else if (parse.triage) {
        toast.error('Parsed, but no confident deal match — pick the deal below');
      }
      onResolved?.();
    } catch (err) {
      toast.error(err.body?.error || err.message || 'Parse failed (the failure alert has fired)');
    } finally {
      setParsing(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-border bg-surface px-3.5 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text">{doc.subject || doc.filename || 'Quote-like mail'}</p>
        <p className="text-[11px] text-text-muted">
          from {doc.from_name || doc.from_address}
          {doc.filename ? ` · ${doc.filename}` : ' · no attachment'}
          {doc.received_at ? ` · ${formatDateOrEmpty(doc.received_at)}` : ''}
        </p>
      </div>
      <Badge variant="outline">detected</Badge>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-[5px] border border-border px-2.5 py-1 text-[12px] text-text hover:border-border-strong"
        >
          <Mail size={13} strokeWidth={1.5} /> Open in Gmail <ExternalLink size={11} strokeWidth={1.5} />
        </a>
      ) : null}
      <Button variant="outline" size="sm" disabled={parsing} onClick={reviewParse}>
        <FileSearch size={14} strokeWidth={1.5} /> {parsing ? 'Parsing…' : 'Review parse'}
      </Button>
    </div>
  );
}

export default function QuoteInbox() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // ?review=<quoteId> deep link (Ship 4a): the queue's "Review parse" action
  // lands with the review sheet already open on the exact draft.
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviewQuoteId, setReviewQuoteId] = useState(() => searchParams.get('review') || null);

  const refresh = useCallback(() => {
    getQuoteInbox()
      .then((d) => { setData(d); setError(''); })
      .catch((err) => setError(err.body?.error || err.message || 'Load failed'));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const drafts = data?.drafts || [];
  const triage = data?.triage || [];
  const detected = data?.detected || [];

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold text-text">Quote inbox</h2>
      <p className="mt-1 text-sm text-text-muted">
        Carrier quotes parsed to drafts. Approving a review is the only thing that writes a quote.
      </p>

      {error ? (
        <p className="mt-6 text-sm text-danger">{error}</p>
      ) : data === null ? (
        <p className="mt-6 text-sm text-text-muted">Checking the inbound queue…</p>
      ) : drafts.length === 0 && triage.length === 0 && detected.length === 0 ? (
        <div className="mt-6 rounded-card border border-dashed border-border p-6 text-sm text-text-muted">
          Nothing waiting. New carrier quote mail and dropped PDFs land here as parse drafts;
          unmatched ones wait for you to pick the deal.
        </div>
      ) : (
        <div className="mt-4 space-y-6">
          {drafts.length > 0 && (
            <section>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-dim">
                Parse drafts · awaiting review
              </h3>
              <div className="space-y-2">
                {drafts.map((d) => (
                  <div key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-border bg-surface px-3.5 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-text">
                        {(d.crm_deal_id || d.deal_id) ? (
                          <Link to={`/account/${d.crm_deal_id || d.deal_id}/deal/${d.crm_deal_id || d.deal_id}`} className="hover:underline">
                            {d.named_insured || d.crm_deal_id || d.deal_id}
                          </Link>
                        ) : (d.named_insured || 'Unlinked draft')}
                        <span className="ml-2 text-xs text-text-muted">{d.carrier}</span>
                      </p>
                      <p className="text-[11px] text-text-muted">
                        {d.option_count} option{d.option_count === 1 ? '' : 's'}
                        {d.round > 1 ? ` · round ${d.round}` : ''}
                        {d.quote_number ? ` · #${d.quote_number}` : ''}
                        {d.quote_expires_at ? ` · offer expires ${formatDateOrEmpty(d.quote_expires_at)}` : ''}
                      </p>
                    </div>
                    <Badge variant="warning">Parse draft</Badge>
                    <Button variant="outline" size="sm" onClick={() => setReviewQuoteId(d.id)}>
                      <FileSearch size={14} strokeWidth={1.5} /> Review parse
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {triage.length > 0 && (
            <section>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-dim">
                Needs a match · pick the deal
              </h3>
              <div className="space-y-2">
                {triage.map((t) => (
                  <TriageRow key={t.id} doc={t} onResolved={refresh} />
                ))}
              </div>
            </section>
          )}

          {detected.length > 0 && (
            <section>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-dim">
                Detected quote mail · not yet parsed
              </h3>
              <div className="space-y-2">
                {detected.map((d) => (
                  <DetectedRow
                    key={d.id}
                    doc={d}
                    onStaged={(quoteId) => setReviewQuoteId(quoteId)}
                    onResolved={refresh}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <QuoteParseReviewSheet
        quoteId={reviewQuoteId}
        open={!!reviewQuoteId}
        onOpenChange={(o) => {
          if (!o) {
            setReviewQuoteId(null);
            if (searchParams.has('review')) setSearchParams({}, { replace: true });
          }
        }}
        onResolved={refresh}
      />
    </div>
  );
}
